import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ChevronLeft, ChevronRight, Rocket, Loader2, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/atoms/button';
import { getAmountByCurrency } from '@/lib/utils';
import { useConfigStore } from '@/lib/stores/config.store';
import { useCreateCampaign } from '../../hooks/use-create-campaign';
import { useAdTypeConfigs } from '../../hooks/use-ad-type-configs';
import { useAdWallet } from '../../hooks/use-ad-wallet';
import { useShopSubscribersCount, useProductLikersCount } from '../../hooks/use-audience-count';
import { useShops } from '@/features/shops/hooks/use-shops';

import { useCampaignWizardStore } from '../../stores/campaign-wizard.store';
import {
  toEur,
  fromEur,
  isNotificationType,
  isBannerType,
  requiresProducts,
  isFeaturedShopType,
  isCpcType,
  calculateCampaignCost,
  getTypeConfig,
} from '../../utils/pricing.utils';
import { generateBannerLinkFromAction } from '../../utils/campaign-link.utils';

import { WizardProgress } from './WizardProgress';
import { StepTypeSelection, CAMPAIGN_TYPE_OPTIONS } from './steps/StepTypeSelection';
import { StepProductSelection } from './steps/StepProductSelection';
import { StepBannerUpload } from './steps/StepBannerUpload';
import { StepBudgetDates } from './steps/StepBudgetDates';
import { StepSummary } from './steps/StepSummary';

// ============ Main Component ============

export function CampaignWizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currency } = useConfigStore();

  // Store state and actions
  const {
    currentStep,
    data,
    filterObjective,
    setCurrentStep,
    setType,
    setProducts,
    setSelectedShop,
    setTitle,
    setMessage,
    setBudget,
    setDailyBudget,
    setBidAmount,
    setStartDate,
    setEndDate,
    setBannerImage,
    setBannerAction,
    setHeadline,
    resetWizard,
  } = useCampaignWizardStore();

  // API hooks
  const { mutate: createCampaign, isPending: isCreating } = useCreateCampaign();
  const { data: typeConfigs } = useAdTypeConfigs();
  const { data: wallet, isLoading: isLoadingWallet } = useAdWallet();
  const { data: subscribersData, isLoading: loadingSubscribers } = useShopSubscribersCount();
  const { data: likersData, isLoading: loadingLikers } = useProductLikersCount();
  const { data: shops = [] } = useShops();

  // Get shop info for banner action
  const shopRefId = shops[0]?.refId;
  const shopName = shops[0]?.name || 'Ma boutique';

  // Step labels
  const stepLabels = React.useMemo(() => {
    if (!data.type) {
      return [
        t('ads.wizard.steps.type', 'Type'),
        t('ads.wizard.steps.selection', 'Sélection'),
        t('ads.wizard.steps.budget', 'Budget'),
        t('ads.wizard.steps.summary', 'Résumé'),
      ];
    }

    // Customize step 2 label based on campaign type
    const isBanner = isBannerType(data.type);
    const step2Label = isBanner
      ? t('ads.wizard.steps.image', 'Image')
      : t('ads.wizard.steps.selection', 'Sélection');

    return [
      t('ads.wizard.steps.type', 'Type'),
      step2Label,
      t('ads.wizard.steps.budget', 'Budget'),
      t('ads.wizard.steps.summary', 'Résumé'),
    ];
  }, [data.type, t]);

  // Get audience count
  const audienceCount = React.useMemo(() => {
    if (data.type === 'push_shop_subscribers') {
      return subscribersData?.count ?? 0;
    }
    if (data.type === 'push_product_likers' || data.type === 'push_single_product_likers') {
      return likersData?.count ?? 0;
    }
    return 0;
  }, [data.type, subscribersData, likersData]);

  const isLoadingAudience = loadingSubscribers || loadingLikers;

  // Get wallet balance in user's currency
  const walletBalance = React.useMemo(() => {
    return getAmountByCurrency(currency, {
      eur: wallet?.balanceEur,
      usd: wallet?.balanceUsd,
      kmf: wallet?.balanceKmf,
    });
  }, [wallet, currency]);

  // Calculate final cost
  const { finalCost, isBelowMinimumBudget } = React.useMemo(() => {
    if (!data.type) return { finalCost: 0, isBelowMinimumBudget: false };
    const config = getTypeConfig(data.type, typeConfigs);
    const { calculatedCost, isBelowMinimum } = calculateCampaignCost(
      data.type,
      config,
      currency,
      {
        audienceCount,
        startDate: data.startDate,
        endDate: data.endDate,
        budget: data.budget,
      }
    );
    return { finalCost: calculatedCost ?? data.budget, isBelowMinimumBudget: isBelowMinimum };
  }, [data, typeConfigs, currency, audienceCount]);

  // Check if wallet balance is sufficient
  const hasInsufficientBalance = !isLoadingWallet && walletBalance < finalCost;

  // Validation for each step
  const canProceed = React.useMemo(() => {
    switch (currentStep) {
      case 0:
        return data.type !== null;
      case 1: {
        if (!data.type) return false;

        // Banner types require an image
        if (isBannerType(data.type)) {
          return data.bannerImagePreview !== null;
        }

        // Featured shop requires a shop selection
        if (isFeaturedShopType(data.type)) {
          return data.selectedShop !== null;
        }

        // For campaigns that require products
        if (requiresProducts(data.type) && data.products.length === 0) {
          return false;
        }

        // For notification campaigns, require a message
        if (isNotificationType(data.type) && !data.message.trim()) {
          return false;
        }

        return true;
      }
      case 2: {
        if (!data.type) return false;
        const isNotification = isNotificationType(data.type);
        const isCpc = isCpcType(data.type);

        if (!data.startDate) return false;
        if (!isNotification && !data.endDate) return false;
        if (isCpc && (!data.bidAmount || data.bidAmount <= 0)) return false;
        if (!isNotification && data.budget <= 0) return false;
        return true;
      }
      case 3:
        return true;
      default:
        return false;
    }
  }, [currentStep, data]);

  // Navigation handlers
  const handleNext = () => {
    if (currentStep < 3 && canProceed) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    } else {
      navigate('/ads/campaigns');
    }
  };

  // Submit handler
  const handleSubmit = () => {
    if (!data.type) return;

    const isNotification = isNotificationType(data.type);
    const isBanner = isBannerType(data.type);
    const typeOption = CAMPAIGN_TYPE_OPTIONS.find((t) => t.value === data.type);
    const serverConfig = getTypeConfig(data.type, typeConfigs);

    // Calculate budget for notifications using server config
    let finalBudgetEur = toEur(data.budget, currency);
    if (isNotification && serverConfig) {
      finalBudgetEur = (audienceCount / 1000) * serverConfig.priceEur;
    }

    // Ensure budget is a valid number
    if (isNaN(finalBudgetEur) || finalBudgetEur < 0) {
      finalBudgetEur = 0;
    }

    // Convert budget to all currencies
    const budgetUsd = fromEur(finalBudgetEur, 'USD');
    const budgetKmf = fromEur(finalBudgetEur, 'KMF');

    const dailyBudgetEur = data.dailyBudget ? toEur(data.dailyBudget, currency) : undefined;
    const dailyBudgetUsd = dailyBudgetEur ? fromEur(dailyBudgetEur, 'USD') : undefined;
    const dailyBudgetKmf = dailyBudgetEur ? fromEur(dailyBudgetEur, 'KMF') : undefined;

    const bidAmountEur = data.bidAmount ? toEur(data.bidAmount, currency) : undefined;
    const bidAmountUsd = bidAmountEur ? fromEur(bidAmountEur, 'USD') : undefined;
    const bidAmountKmf = bidAmountEur ? fromEur(bidAmountEur, 'KMF') : undefined;

    // Build name
    const name = data.name || `${typeOption?.label || data.type} - ${format(new Date(), 'dd/MM/yyyy')}`;

    // Build items - always ensure it's an array
    let items: Array<{
      bannerLink?: string;
      bannerUrl?: string;
      productRefId?: string;
      message?: string;
      title?: string;
    }> = [];

    if (isBanner) {
      // For banner/carousel, include the banner action link
      const bannerLink = shopRefId
        ? generateBannerLinkFromAction(data.bannerAction, shopRefId)
        : `/shop/${shopRefId}`;

      items = [
        {
          bannerLink,
          productRefId: data.bannerAction.type === 'product' ? data.bannerAction.productRefId : undefined,
          // Use headline as title for display, fallback to campaign name
          title: data.headline || name,
        },
      ];
    } else if (isFeaturedShopType(data.type) && data.selectedShop) {
      items = [
        {
          bannerUrl: data.selectedShop.logo,
          title: data.selectedShop.name,
          bannerLink: `/shops/${data.selectedShop.id}`,
        },
      ];
    } else if (data.products.length > 0) {
      items = data.products.map((p) => ({
        productRefId: p.refId,
        message: data.message || undefined,
        title: data.title || name,
      }));
    } else if (data.message) {
      items = [{ message: data.message, title: data.title || name }];
    }

    // Build campaign data
    const campaignData = {
      name,
      type: data.type,
      budgetEur: Math.round(finalBudgetEur * 100) / 100,
      budgetUsd: Math.round(budgetUsd * 100) / 100,
      budgetKmf: Math.round(budgetKmf),
      dailyBudgetEur: dailyBudgetEur ? Math.round(dailyBudgetEur * 100) / 100 : undefined,
      dailyBudgetUsd: dailyBudgetUsd ? Math.round(dailyBudgetUsd * 100) / 100 : undefined,
      dailyBudgetKmf: dailyBudgetKmf ? Math.round(dailyBudgetKmf) : undefined,
      bidAmountEur: bidAmountEur ? Math.round(bidAmountEur * 100) / 100 : undefined,
      bidAmountUsd: bidAmountUsd ? Math.round(bidAmountUsd * 100) / 100 : undefined,
      bidAmountKmf: bidAmountKmf ? Math.round(bidAmountKmf) : undefined,
      startDate: data.startDate,
      endDate: isNotification ? data.startDate : data.endDate,
      items,
    };

    // Use the appropriate signature based on whether there's a banner image
    const mutationParams = isBanner && data.bannerImage
      ? { data: campaignData, bannerImage: data.bannerImage }
      : campaignData;

    createCampaign(mutationParams, {
      onSuccess: (result) => {
        resetWizard();
        if (result?.refId) {
          navigate(`/ads/campaigns/${result.refId}`);
        } else {
          navigate('/ads/campaigns');
        }
      },
    });
  };

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <StepTypeSelection
            selectedType={data.type}
            onSelectType={setType}
            currency={currency}
            typeConfigs={typeConfigs}
            filterObjective={filterObjective}
            audienceData={{
              subscribersCount: subscribersData?.count ?? 0,
              likersCount: likersData?.count ?? 0,
              isLoading: loadingSubscribers || loadingLikers,
            }}
          />
        );

      case 1:
        if (!data.type) return null;

        // Banner types show image upload step
        if (isBannerType(data.type)) {
          return (
            <StepBannerUpload
              campaignType={data.type}
              bannerImage={data.bannerImage}
              bannerImagePreview={data.bannerImagePreview}
              onBannerImageChange={setBannerImage}
              bannerAction={data.bannerAction}
              onBannerActionChange={setBannerAction}
              headline={data.headline}
              onHeadlineChange={setHeadline}
              shopName={shopName}
              shopRefId={shopRefId}
              products={data.products}
            />
          );
        }

        // Other types show product/message/shop selection
        return (
          <StepProductSelection
            campaignType={data.type}
            selectedProducts={data.products}
            onProductsChange={setProducts}
            selectedShop={data.selectedShop}
            onShopChange={setSelectedShop}
            title={data.title}
            onTitleChange={setTitle}
            message={data.message}
            onMessageChange={setMessage}
          />
        );

      case 2:
        if (!data.type) return null;
        return (
          <StepBudgetDates
            campaignType={data.type}
            budget={data.budget}
            onBudgetChange={setBudget}
            dailyBudget={data.dailyBudget}
            onDailyBudgetChange={setDailyBudget}
            bidAmount={data.bidAmount}
            onBidAmountChange={setBidAmount}
            startDate={data.startDate}
            onStartDateChange={setStartDate}
            endDate={data.endDate}
            onEndDateChange={setEndDate}
            currency={currency}
            typeConfigs={typeConfigs}
            audienceCount={audienceCount}
            isLoadingAudience={isLoadingAudience}
          />
        );

      case 3:
        return (
          <StepSummary
            data={data}
            currency={currency}
            onEdit={setCurrentStep}
            typeConfigs={typeConfigs}
            audienceCount={audienceCount}
            walletBalance={walletBalance}
            isLoadingWallet={isLoadingWallet}
          />
        );

      default:
        return null;
    }
  };

  return (
    <div className="max-w-2xl mx-auto pb-24 sm:pb-0">
      {/* Progress indicator */}
      <WizardProgress
        currentStep={currentStep}
        totalSteps={4}
        stepLabels={stepLabels}
        className="mb-6 sm:mb-8"
      />

      {/* Step content */}
      <div className="min-h-[300px] sm:min-h-[400px]">{renderStepContent()}</div>

      {/* Desktop Navigation */}
      <div className="hidden sm:flex items-center justify-between pt-6 mt-8 border-t">
        <Button type="button" variant="outline" onClick={handleBack}>
          <ChevronLeft className="size-4 mr-1" />
          {currentStep === 0 ? t('common.cancel') : t('common.back', 'Retour')}
        </Button>

        {currentStep < 3 ? (
          <Button type="button" onClick={handleNext} disabled={!canProceed}>
            {t('common.continue', 'Continuer')}
            <ChevronRight className="size-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isCreating || hasInsufficientBalance || isBelowMinimumBudget}
            className="bg-primary"
          >
            {isCreating ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : isBelowMinimumBudget ? (
              <>
                <AlertTriangle className="size-4 mr-2" />
                Budget minimum non atteint
              </>
            ) : hasInsufficientBalance ? (
              <>
                <AlertTriangle className="size-4 mr-2" />
                Solde insuffisant
              </>
            ) : (
              <>
                <Rocket className="size-4 mr-2" />
                {t('ads.wizard.launch', 'Lancer la campagne')}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Mobile: Sticky bottom navigation */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-background border-t p-4 flex items-center justify-between gap-3 z-40">
        <Button
          type="button"
          variant="outline"
          onClick={handleBack}
          className="flex-1"
        >
          <ChevronLeft className="size-4 mr-1" />
          {currentStep === 0 ? t('common.cancel') : t('common.back', 'Retour')}
        </Button>

        {currentStep < 3 ? (
          <Button
            type="button"
            onClick={handleNext}
            disabled={!canProceed}
            className="flex-1"
          >
            {t('common.continue', 'Continuer')}
            <ChevronRight className="size-4 ml-1" />
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={isCreating || hasInsufficientBalance || isBelowMinimumBudget}
            className="flex-1 bg-primary"
          >
            {isCreating ? (
              <>
                <Loader2 className="size-4 mr-2 animate-spin" />
                {t('common.loading')}
              </>
            ) : isBelowMinimumBudget || hasInsufficientBalance ? (
              <>
                <AlertTriangle className="size-4 mr-2" />
                {isBelowMinimumBudget ? 'Budget min.' : 'Solde insuf.'}
              </>
            ) : (
              <>
                <Rocket className="size-4 mr-2" />
                {t('ads.wizard.launch', 'Lancer')}
              </>
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
